import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject, interval } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { MapConsentService } from 'src/app/shared/map-consent/shared/map-consent.service';


/**
 * SessionService für Multi-Tab Session-Management
 * 
 * WICHTIG: Tabs bleiben aktiv auch wenn im Hintergrund!
 * - Tab-Sichtbarkeit spielt KEINE Rolle für Inaktivitäts-Timer
 * - Timer startet NUR wenn ALLE Tabs geschlossen sind
 * - Tabs im Hintergrund bleiben ONLINE (perfekt für Monitoring-Tools)
 * 
 * VERBESSERUNGEN v2:
 * ✅ Tab zählt sich SELBST mit in activeTabs
 * ✅ Timer startet NICHT wenn noch ein Tab offen ist
 * ✅ Cleanup überspringt eigene Tab
 * ✅ Heartbeat updated auch eigenen Timestamp
 */


interface TabHeartbeat {
  tabId: string;
  timestamp: number;
  isActive: boolean;
}


interface SessionMessage {
  type: 'heartbeat' | 'tab-open' | 'tab-close' | 'logout' | 'check-session' | 'sync-request' | 'sync-response' | 'confirm-last-tab';
  tabId: string;
  timestamp: number;
  payload?: any;
}


@Injectable({
  providedIn: 'root'
})
export class SessionService {
  // Konstanten
  private readonly INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 Minuten
  private readonly HEARTBEAT_INTERVAL = 30 * 1000; // 30 Sekunden
  private readonly SYNC_CHECK_INTERVAL = 5 * 1000; // 5 Sekunden
  private readonly SESSION_CHANNEL_NAME = 'session-channel';
  private readonly SESSION_ID_KEY = 'session-id';


  // Properties
  private tabId?: string;
  private sessionId?: string;
  private channel?: BroadcastChannel;
  private heartbeatTimer: any;
  private inactivityTimer: any;
  private syncCheckTimer: any;
  private syncTimeout: any;
  private activeTabs: Map<string, TabHeartbeat> = new Map();
  private isInitialized = false;
  private lastSyncCheckTime = 0;
  private lastTabsCount = 0;


  // Subjects für externe Komponenten
  private onSessionExpired$ = new Subject<void>();
  private isLoggedIn$ = new BehaviorSubject<boolean>(false);
  private activeTabCount$ = new BehaviorSubject<number>(0);


  constructor(
    private http: HttpClient,
    private ngZone: NgZone,
    private mapConsentService: MapConsentService
  ) {
    // Nichts hier
  }


  /**
   * Initialisiert die Session-Verwaltung
   * MUSS nach dem Login aufgerufen werden
   */
  public initializeSession(): void {
    if (this.isInitialized) {
/*       console.log('ℹ️ Session bereits initialisiert - überspringen');
 */      return;
    }


    this.isInitialized = true;


    this.tabId = this.generateTabId();
    this.sessionId = this.getOrCreateSessionId();


/*     console.log(`📱 Tab-ID: ${this.tabId}`);
    console.log(`🔑 Session-ID: ${this.sessionId}`);
 */

    this.setupBroadcastChannel();
    this.registerTab();
    this.startHeartbeat();
    this.startSyncCheck();
    this.monitorTabVisibility();
    this.monitorTabClose();


    this.isLoggedIn$.next(true);
  }


  /**
   * Erstellt den BroadcastChannel für Multi-Tab Kommunikation
   */
  private setupBroadcastChannel(): void {
    if (!window.BroadcastChannel) {
      console.warn('BroadcastChannel API nicht unterstützt. Session-Sync funktioniert eingeschränkt.');
      return;
    }


    this.channel = new BroadcastChannel(this.SESSION_CHANNEL_NAME);


    this.channel.onmessage = (event: MessageEvent<SessionMessage>) => {
      this.handleChannelMessage(event.data);
    };


    this.channel.onmessageerror = (error) => {
      console.error('BroadcastChannel Fehler:', error);
    };
  }


  /**
   * Behandelt eingehende Nachrichten von anderen Tabs
   */
  private handleChannelMessage(message: SessionMessage): void {
    if (message.tabId === this.tabId) {
      return;
    }


    switch (message.type) {
      case 'heartbeat':
        this.activeTabs.set(message.tabId, {
          tabId: message.tabId,
          timestamp: message.timestamp,
          isActive: message.payload?.isActive ?? true
        });
        
        // ✅ Wenn Heartbeat kommt und Timer läuft → abbrechen!
        if (this.inactivityTimer) {
/*           console.log('🔄 Heartbeat von anderer Tab - Inaktivitäts-Timer gestoppt');
 */          clearTimeout(this.inactivityTimer);
          this.inactivityTimer = null;
        }
        
        this.updateActiveTabCount();
        break;


      case 'tab-open':
/*         console.log(`📖 Tab geöffnet: ${message.tabId}`);
 */        this.activeTabs.set(message.tabId, {
          tabId: message.tabId,
          timestamp: message.timestamp,
          isActive: true
        });
        
        // ✅ Stoppe Timer wenn neue Tab öffnet
        if (this.inactivityTimer) {
/*           console.log('🔄 Tab geöffnet - Inaktivitäts-Timer gestoppt');
 */          clearTimeout(this.inactivityTimer);
          this.inactivityTimer = null;
        }
        
        this.updateActiveTabCount();
        break;


      case 'tab-close':
/*         console.log(`❌ Tab geschlossen: ${message.tabId}`);
 */        this.activeTabs.delete(message.tabId);
        this.updateActiveTabCount();
        
        // ✅ WICHTIG: activeTabs.size === 1 bedeutet "nur ich noch offen"
        // Weil ich mich selbst mitzähle!
        if (this.activeTabs.size === 1) {
/*           console.log('⚠️ Alle anderen Tabs geschlossen - aber ICH bin noch offen');
          console.log('   → Timer wird NICHT gestartet (Tab noch aktiv)');
 */        }
        break;


      case 'logout':
/*         console.log('🚨 Logout von anderer Tab empfangen');
 */        this.performLogout();
        break;


      case 'check-session':
        this.sendMessage({
          type: 'heartbeat',
          tabId: this.tabId!,
          timestamp: Date.now(),
          payload: { isActive: true }
        });
        break;


      case 'sync-request':
/*         console.log(`🔄 Sync-Request von ${message.tabId}`);
 */        this.sendMessage({
          type: 'sync-response',
          tabId: this.tabId!,
          timestamp: Date.now(),
          payload: {
            myCount: this.activeTabs.size,
            myTabs: Array.from(this.activeTabs.keys()),
            senderTabId: message.tabId
          }
        });
        break;


      case 'sync-response':
        this.handleSyncResponse(message);
        break;


      case 'confirm-last-tab':
/*         console.log(`🔍 Bestätigung angefordert von ${message.tabId}`);
 */        this.sendMessage({
          type: 'heartbeat',
          tabId: this.tabId!,
          timestamp: Date.now(),
          payload: { isActive: true }
        });
        break;
    }
  }


  /**
   * Startet regelmäßigen Synchronisations-Check
   */
  private startSyncCheck(): void {
    if (this.syncCheckTimer) {
      clearInterval(this.syncCheckTimer);
    }


    this.syncCheckTimer = setInterval(() => {
      this.performSyncCheck();
    }, this.SYNC_CHECK_INTERVAL);
  }


  /**
   * Führt Sync-Check durch
   */
  private performSyncCheck(): void {
    const now = Date.now();
    
    if (now - this.lastSyncCheckTime < 3000) {
      return;
    }


    const localCount = this.activeTabs.size;
    this.cleanupInactiveTabs();
    const countAfterCleanup = this.activeTabs.size;


    if (localCount !== countAfterCleanup) {
/*       console.log(`🔄 Sync-Check: Count geändert ${localCount} → ${countAfterCleanup}`);
 */      this.lastSyncCheckTime = now;
      return;
    }


    // ✅ WICHTIG: size > 1 weil ich mich selbst mitzähle
    if (this.activeTabs.size > 1) {
      this.sendMessage({
        type: 'sync-request',
        tabId: this.tabId!,
        timestamp: now,
        payload: { 
          myCount: this.activeTabs.size,
          myTabs: Array.from(this.activeTabs.keys())
        }
      });


      this.lastSyncCheckTime = now;
    }
  }


  /**
   * Verarbeitet Sync-Responses
   */
  private handleSyncResponse(message: SessionMessage): void {
    const senderTabId = message.tabId;
    const senderCount = message.payload?.myCount ?? 0;
    const senderTabs = message.payload?.myTabs ?? [];
    const localCount = this.activeTabs.size;


    if (senderCount !== localCount || senderTabs.length !== localCount) {
/*       console.log(`⚠️ Sync-Mismatch! Ich: ${localCount}, ${senderTabId}: ${senderCount}`);
 */

      for (const unknownTabId of senderTabs) {
        // ✅ Überspringe meine eigene Tab
        if (unknownTabId === this.tabId) {
          continue;
        }
        
        if (!this.activeTabs.has(unknownTabId)) {
/*           console.log(`🔧 Füge unbekannte Tab hinzu: ${unknownTabId}`);
 */          this.activeTabs.set(unknownTabId, {
            tabId: unknownTabId,
            timestamp: Date.now(),
            isActive: true
          });
        }
      }


      this.updateActiveTabCount();
    }
  }


  /**
   * ✅ GEÄNDERT: Registriert die aktuelle Tab (inkl. sich selbst!)
   */
  private registerTab(): void {
    // ✅ Füge MICH SELBST zur activeTabs hinzu
    this.activeTabs.set(this.tabId!, {
      tabId: this.tabId!,
      timestamp: Date.now(),
      isActive: true
    });


    // Sende tab-open an andere Tabs
    this.sendMessage({
      type: 'tab-open',
      tabId: this.tabId!,
      timestamp: Date.now()
    });


    this.updateActiveTabCount();
    
/*     console.log(`✅ Tab registriert - Total: ${this.activeTabs.size} (inkl. mir selbst)`);
 */  }


  /**
   * ✅ GEÄNDERT: Startet Heartbeat (updated auch eigenen Timestamp)
   */
  private startHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }


    this.heartbeatTimer = setInterval(() => {
      // ✅ Update meinen eigenen Heartbeat Timestamp
      this.activeTabs.set(this.tabId!, {
        tabId: this.tabId!,
        timestamp: Date.now(),
        isActive: document.visibilityState === 'visible'
      });
      
      // Sende Heartbeat an andere Tabs
      this.sendMessage({
        type: 'heartbeat',
        tabId: this.tabId!,
        timestamp: Date.now(),
        payload: { isActive: document.visibilityState === 'visible' }
      });


      this.cleanupInactiveTabs();
    }, this.HEARTBEAT_INTERVAL);
  }


  /**
   * ✅ GEÄNDERT: Entfernt inaktive Tabs (überspringt eigene Tab)
   */
  private cleanupInactiveTabs(): void {
    const now = Date.now();
    const timeout = this.HEARTBEAT_INTERVAL * 2; // 60 Sekunden


    let removedCount = 0;
    for (const [tabId, tab] of this.activeTabs.entries()) {
      // ✅ WICHTIG: Überspringe meine eigene Tab!
      if (tabId === this.tabId) {
        continue;
      }
      
      if (now - tab.timestamp > timeout) {
/*         console.log(`🧹 Inaktive Tab entfernt: ${tabId} (kein Heartbeat seit > 1 Min.)`);
 */        this.activeTabs.delete(tabId);
        removedCount++;
      }
    }


    if (removedCount > 0) {
      this.updateActiveTabCount();


      // ✅ WICHTIG: size === 1 bedeutet "nur ich noch offen"
      if (this.activeTabs.size === 1) {
/*         console.log('⚠️ Alle anderen Tabs inaktiv - aber ICH bin noch da');
        console.log('   → Timer wird NICHT gestartet');
 */      }
    }
  }


  /**
   * Überwacht Tab-Sichtbarkeit (nur für Sync-Check)
   * 
   * WICHTIG: Tab-Sichtbarkeit beeinflusst NICHT den Logout-Timer!
   */
  private monitorTabVisibility(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
/*         console.log('👁️ Tab wird sichtbar (nur Sync-Check)');
 */        
        // Nur Sync-Check triggern
        this.sendMessage({
          type: 'check-session',
          tabId: this.tabId!,
          timestamp: Date.now()
        });
        this.performSyncCheck();
      } else {
/*         console.log('🔇 Tab wird versteckt (bleibt aber AKTIV)');
 */      }
    });
  }


  /**
   * ✅ GEÄNDERT: Überwacht Tab-Schließung
   * Timer startet NUR wenn ALLE Tabs geschlossen sind
   */
  private monitorTabClose(): void {
    window.addEventListener('beforeunload', () => {
/*       console.log('🛑 Tab wird geschlossen');
 */      
      // Sende tab-close an andere Tabs
      this.sendMessage({
        type: 'tab-close',
        tabId: this.tabId!,
        timestamp: Date.now()
      });
      
      // ✅ Entferne mich aus activeTabs
      this.activeTabs.delete(this.tabId!);
      
      // ✅ Prüfe: Bin ich der letzte Tab?
      if (this.activeTabs.size === 0) {
/*         console.log('🔴 Ich war der letzte Tab - setze Timestamp in localStorage');
 */        
        // Setze Timestamp für potentiellen Logout-Check beim nächsten Start
        localStorage.setItem('last-tab-closed-at', Date.now().toString());
      } else {
/*         console.log(`✅ Noch ${this.activeTabs.size} andere Tabs offen - kein Timer`);
 */      }
    });


    window.addEventListener('unload', () => {
      this.cleanup();
    });
  }


  /**
   * Sendet Nachricht über BroadcastChannel
   */
  private sendMessage(message: SessionMessage): void {
    if (this.channel) {
      try {
        this.channel.postMessage(message);
      } catch (error) {
        console.error('Fehler beim Senden von BroadcastChannel Nachricht:', error);
      }
    }
  }


  /**
   * Aktualisiert die Anzahl aktiver Tabs
   */
  private updateActiveTabCount(): void {
    const count = this.activeTabs.size;
    
    if (count !== this.lastTabsCount) {
/*       console.log(`📊 Aktive Tabs: ${count} (inkl. mir selbst)`);
 */      this.lastTabsCount = count;
    }
    
    this.activeTabCount$.next(count);
  }


  /**
   * Generiert eindeutige Tab-ID
   */
  private generateTabId(): string {
    return `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }


  /**
   * Holt oder erstellt Session-ID
   */
  private getOrCreateSessionId(): string {
    let sessionId = localStorage.getItem(this.SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(this.SESSION_ID_KEY, sessionId);
    }
    return sessionId;
  }


  /**
   * Führt Logout durch
   */
  public logout(): void {
    this.sendMessage({
      type: 'logout',
      tabId: this.tabId!,
      timestamp: Date.now()
    });


    this.performLogout();
  }


  /**
   * Interne Logout-Implementierung
   */
  private performLogout(): void {
/*     console.log('🔐 Logout wird durchgeführt...');
 */

    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem(this.SESSION_ID_KEY);
    localStorage.removeItem('last-tab-closed-at');
    localStorage.removeItem('role');
    this.mapConsentService.revokeConsent();
    
    this.cleanup();


    this.isLoggedIn$.next(false);
    this.isInitialized = false;


    this.onSessionExpired$.next();
  }


  /**
   * Cleanup
   */
  private cleanup(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
    if (this.syncCheckTimer) {
      clearInterval(this.syncCheckTimer);
    }
    if (this.syncTimeout) {
      clearTimeout(this.syncTimeout);
    }
    if (this.channel) {
      this.channel.close();
    }
    this.activeTabs.clear();
  }


  // ============================================
  // Public API
  // ============================================


  public getSessionExpired$(): Observable<void> {
    return this.onSessionExpired$.asObservable();
  }


  public isLoggedIn(): Observable<boolean> {
    return this.isLoggedIn$.asObservable();
  }


  public getActiveTabCount$(): Observable<number> {
    return this.activeTabCount$.asObservable();
  }


  public getTabId(): string {
    return this.tabId!;
  }


  public getSessionId(): string {
    return this.sessionId!;
  }


  public getActiveTabCount(): number {
    return this.activeTabs.size;
  }


  public debugInfo(): string {
    return `
    Tab-ID: ${this.tabId}
    Session-ID: ${this.sessionId}
    Ist Initialisiert: ${this.isInitialized}
    Aktive Tabs: ${this.activeTabs.size} (inkl. mir selbst)
    Inactivity Timer aktiv: ${!!this.inactivityTimer}
    Tabs Details: ${JSON.stringify(Array.from(this.activeTabs.entries()))}
    `;
  }
}
